import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MessageCircle, X, Minimize2, Maximize2, Send } from 'lucide-react';
import chatService from '../services/chatService';
import { PROJECT_CONFIG, SUGGESTIONS } from '../config/projectConfig';
import MarkdownRenderer from './MarkdownRenderer';
import './Chat.css';
import { useChat } from '../context/ChatContext';
import { Z_INDEX } from '../const';

const Chat = () => {
  const [messages, setMessages] = useState([]);
  const { isOpen, setIsOpen, isMinimized, setIsMinimized } = useChat();
  const [isTyping, setIsTyping] = useState(false);
  const [inputMessage, setInputMessage] = useState('');
  
  const messagesContainerRef = useRef(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Add new state for device detection
  const [isMobile, setIsMobile] = useState(false);

  const handleStateChanged = useCallback((newState) => {
    // Don't update isOpen from chatService state - let the component manage its own open state
    // setIsOpen(newState.isOpen);
    setIsMinimized(newState.isMinimized);
    setIsTyping(newState.isTyping);
  }, [setIsMinimized, setIsTyping]);

  useEffect(() => {
    // Initialize chat service
    chatService.initialize();
    
    // Define handleMessageAdded inside useEffect to avoid dependency issues
    const handleMessageAdded = (message) => {
      setMessages(prev => {
        // Check if message already exists to prevent duplicates
        const messageExists = prev.some(existingMessage => existingMessage.id === message.id);
        if (messageExists) {
          return prev; // Don't add duplicate
        }
        const newMessages = [...prev, message];
        // Scroll after state update
        setTimeout(() => scrollToBottom(), 100);
        return newMessages;
      });
    };

    // Set up event listeners
    chatService.addEventListener('messageAdded', handleMessageAdded);
    chatService.addEventListener('stateChanged', handleStateChanged);
    
    // Get initial state - only set messages if we don't have any yet
    const initialState = chatService.getState();
    if (messages.length === 0) {
      setMessages(initialState.messages);
    }
    // Don't set isOpen from chatService - let component manage its own state
    // setIsOpen(initialState.isOpen);
    setIsMinimized(initialState.isMinimized);
    setIsTyping(initialState.isTyping);

    return () => {
      chatService.removeEventListener('messageAdded', handleMessageAdded);
      chatService.removeEventListener('stateChanged', handleStateChanged);
    };
  }, [messages.length, handleStateChanged, setIsMinimized]); // Add messages.length as dependency to properly track initialization

  useEffect(() => {
    scrollToBottom();
  }, [messages, messages.length,isOpen]);

  useEffect(() => {
    if (isTyping) {
      const timeoutId = setTimeout(scrollToBottom, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [isTyping]);

  useEffect(() => {
    const checkOrientation = () => {
      setIsMobile(window.innerWidth < 768);
    };

    // Check on mount and add listener
    checkOrientation();
    window.addEventListener('resize', checkOrientation);

    return () => window.removeEventListener('resize', checkOrientation);
  }, []);

  useEffect(() => {
    if (isMobile && isOpen) {
      setIsOpen(false);
    }
  }, [isMobile, isOpen, setIsOpen]);

  const scrollToBottom = () => {
    if (messagesEndRef.current && messagesContainerRef.current) {
      const scrollContainer = messagesContainerRef.current;
      const scrollHeight = scrollContainer.scrollHeight;
      const height = scrollContainer.clientHeight;
      const maxScroll = scrollHeight - height;
      
      scrollContainer.scrollTo({
        top: maxScroll,
        behavior: 'smooth'
      });
    }
  };

  const toggleChat = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString([], { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const sendMessage = async () => {
    if (!inputMessage.trim() || isTyping) return;
    setInputMessage('');
    await chatService.sendMessage(inputMessage);
    // Scroll after sending message
    setTimeout(() => scrollToBottom(), 100);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleSuggestionClick = async (suggestion) => {
    if (isTyping) return;
    
    try {
      // Send the message
      await chatService.sendMessage(suggestion);
      
      // Scroll to bottom after sending
      setTimeout(() => scrollToBottom(), 100);
    } catch (error) {
      console.error('Failed to send suggestion:', error);
      // Keep the suggestion in input if it failed
      setInputMessage(suggestion);
    }
  };

  const toggleMinimized = () => {
    setIsMinimized(!isMinimized);
  };

  const closeChat = () => {
    setIsOpen(false);
  };

  return (
    <>
      {/* Chat Icon */}
      {!isOpen && (
        <button
          onClick={toggleChat}
          className={`fixed bg-gradient-to-br from-gray-100 via-white to-gray-200 hover:from-gray-200 hover:to-gray-100 text-gray-700 p-4 rounded-full shadow-xl border border-gray-200 transition-all duration-200 hover:scale-105 z-50 group
            ${isMobile ? 'bottom-20 right-4' : 'bottom-20 right-8'}`}
          aria-label="Open chat assistant"
          style={{ zIndex: Z_INDEX.CHAT_BUTTON }}
        >
          <MessageCircle size={24} className="text-gray-600 group-hover:text-gray-800" />
          <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full border-2 border-white"></div>
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div
          className={`fixed transition-all duration-300 z-50 bg-gradient-to-br from-white via-gray-50 to-gray-100 rounded-2xl shadow-2xl border border-gray-200 flex flex-col
            ${isMinimized ? 'h-16' : isMobile ? 'h-[65vh]' : 'h-[600px]'}
            ${isMobile ? (
              isMinimized 
                ? 'w-[90vw] bottom-16 right-4' 
                : 'w-[94vw] h-[75vh] bottom-16 right-[3vw] left-[3vw] rounded-2xl'
            ) : (
              isMinimized ? 'w-80 bottom-24 right-8' : 'w-96 bottom-24 right-8'
            )}`}
          style={{ 
            zIndex: Z_INDEX.CHAT_WINDOW,
            maxHeight: isMobile && !isMinimized ? 'calc(100vh - 96px)' : undefined 
          }}
        >
          {/* Chat Header */}
          <div className={`flex items-center justify-between p-5 border-b border-gray-100 bg-white/80 backdrop-blur-md flex-shrink-0 ${isMobile ? 'rounded-t-2xl' : 'rounded-t-2xl'}`}>
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 rounded-full flex items-center justify-center shadow-md p-1">
                <img 
                  src={`${process.env.PUBLIC_URL}/impactIcon.png`} 
                  alt="InnPact Solutions" 
                  className="w-full h-full object-cover"
                />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 text-sm">{PROJECT_CONFIG.assistant.name}</h3>
                <p className="text-xs text-gray-500">Online</p>
              </div>
            </div>
            <div className="flex items-center space-x-1">
              <button
                onClick={toggleMinimized}
                className="hover:bg-gray-200 p-2 rounded-lg transition-colors text-gray-500 hover:text-gray-700"
                aria-label={isMinimized ? "Expand chat" : "Minimize chat"}
              >
                {isMinimized ? <Maximize2 size={16} /> : <Minimize2 size={16} />}
              </button>
              <button
                onClick={closeChat}
                className="hover:bg-gray-200 p-2 rounded-lg transition-colors text-gray-500 hover:text-gray-700"
                aria-label="Close chat"
              >
                <X size={16} />
              </button>
            </div>
          </div>

          {/* Chat Content */}
          {!isMinimized && (
            <div className="flex flex-col flex-1 overflow-hidden">
              {/* Messages Area */}
              <div 
                ref={messagesContainerRef}
                className={`flex-1 p-6 overflow-y-auto bg-transparent min-h-0`}
                style={{
                  maxHeight: isMobile ? 'calc(85vh - 180px)' : undefined
                }}
              >
                <div className="space-y-4">
                  {messages.map((message) => (
                    <div
                      key={message.id}
                      className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div className={`max-w-[85%] ${message.sender === 'user' ? 'order-2' : 'order-1'}`}>
                        {message.sender === 'bot' && (
                          <div className="flex items-center space-x-2 mb-1">
                            <div className="w-6 h-6 rounded-full flex items-center justify-center shadow p-1">
                              <img 
                                src={`${process.env.PUBLIC_URL}/impactIcon.png`} 
                                alt="InnPact Solutions" 
                                className="w-full h-full object-cover"
                              />
                            </div>
                            <span className="text-xs text-gray-500 font-medium">
                              {PROJECT_CONFIG.assistant.name}
                            </span>
                          </div>
                        )}
                        <div
                          className={`p-4 rounded-2xl text-sm leading-relaxed shadow-sm ${
                            message.sender === 'user'
                              ? 'bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white rounded-br-md'
                              : 'bg-white/80 text-gray-800 rounded-bl-md border border-gray-100 backdrop-blur-md'
                          }`}
                          style={{ marginBottom: 2 }}
                        >
                          {message.sender === 'user' ? (
                            <p className="whitespace-pre-wrap">{message.text}</p>
                          ) : (
                            <MarkdownRenderer content={message.text} />
                          )}
                        </div>
                        <div 
                          className={`flex items-center space-x-2 mt-1 text-xs text-gray-400 ${
                            message.sender === 'user' ? 'justify-end' : 'justify-start'
                          }`}
                          style={{ marginTop: 4 }}
                        >
                          <span>{formatTimestamp(message.timestamp)}</span>
                        </div>
                      </div>
                    </div>
                  ))}

                  {/* Typing Indicator */}
                  {isTyping && (
                    <div className="flex justify-start">
                      <div className="max-w-[85%]">
                        <div className="flex items-center space-x-2 mb-1">
                          <div className="w-6 h-6 rounded-full flex items-center justify-center shadow overflow-hidden p-1">
                            <img 
                              src={`${process.env.PUBLIC_URL}/impactIcon.png`} 
                              alt="InnPact Solutions" 
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <span className="text-xs text-gray-500 font-medium">{PROJECT_CONFIG.assistant.name} is typing...</span>
                        </div>
                        <div className="bg-white/80 border border-gray-100 p-4 rounded-2xl rounded-bl-md shadow-sm">
                          <div className="flex space-x-1">
                            <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                            <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                            <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  <div ref={messagesEndRef} style={{ height: '1px', float: 'left', clear: 'both' }} />
                </div>
              </div>

              {/* Quick Suggestions */}
              {messages.length <= 1 && !isMobile && (
                <div className="px-5 py-3 border-t border-gray-100 bg-white/80 backdrop-blur-md flex-shrink-0">
                  <p className="text-xs text-gray-600 mb-2 font-medium">Ask about the project:</p>
                  <div className="flex flex-wrap gap-2">
                    {SUGGESTIONS.slice(0, 3).map((suggestion, index) => (
                      <button
                        key={index}
                        onClick={() => handleSuggestionClick(suggestion)}
                        className="px-3 py-2 text-xs bg-white border border-gray-200 rounded-lg hover:border-gray-300 hover:bg-gray-50 transition-colors text-gray-700 cursor-pointer shadow-sm"
                        type="button"
                      >
                        {suggestion}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Input Area */}
              <div className={`p-4 border-t border-gray-100 bg-white/90 backdrop-blur-md flex-shrink-0 rounded-b-2xl`}>
                <div className="flex space-x-3">
                  <input
                    ref={inputRef}
                    type="text"
                    value={inputMessage}
                    onChange={(e) => setInputMessage(e.target.value)}
                    onKeyDown={handleKeyPress}
                    placeholder={`Ask about the ${PROJECT_CONFIG.project.shortName} project...`}
                    className="text-black flex-1 px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm placeholder-gray-500 bg-white/80 backdrop-blur-md shadow-sm"
                  />
                  <button
                    onClick={sendMessage}
                    disabled={!inputMessage.trim() || isTyping}
                    className="bg-gray-900 hover:bg-gray-800 disabled:bg-gray-300 text-white p-2.5 rounded-xl transition-colors disabled:cursor-not-allowed flex items-center justify-center shadow-md"
                    aria-label="Send message"
                    type="button"
                  >
                    <Send size={16} />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default Chat; 